import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { Table, TableBody, TableRow, TableBodyCell, Spinner, CroppableContent, EmptyState } from '@shipengine/giger';
import { useTranslation } from '@packlink/translation-provider';
import { Currency } from '@packlink/packlink-sdk';
import { useTransactions } from '../../hooks/useTransactions';
import { TransactionsTableHeader } from './TransactionsTableHeader';
import { TransactionsTableFooter } from './TransactionsTableFooter';
import { TransactionsFilters, TransactionFilters } from './TransactionsFilters';
import { getTableEmptyStyles, getTableStyles } from './SettingsTransactionsStyles';

export function SettingsTransactions(): JSX.Element {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const dateFormatter = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    const filters = useMemo<TransactionFilters>(
        () => ({
            id: searchParams.get('id') ?? undefined,
            from: searchParams.get('from') ?? undefined,
            to: searchParams.get('to') ?? undefined,
        }),
        [searchParams],
    );

    const { items, pagination, isLoading, isRefetching } = useTransactions(
        filters,
        parseInt(searchParams.get('page') ?? '1', 10),
    );

    const isEmptySearch = Object.values(filters).some((value) => value) && items.length === 0;

    const onFiltersChange = useCallback(
        (newFilters: TransactionFilters, page?: number) => {
            const newSearchParams = generateSearchParams(newFilters, page);

            setSearchParams(newSearchParams);
        },
        [setSearchParams],
    );

    const onPageChange = (page: number) => {
        onFiltersChange(filters, page);
    };

    return (
        <>
            <TransactionsFilters filters={filters} onFiltersChange={onFiltersChange} />

            <Table
                header={<TransactionsTableHeader />}
                footer={<TransactionsTableFooter pagination={pagination} onPageChange={onPageChange} />}
                outerWrapperStyles={getTableStyles(isRefetching)}
            >
                <TableBody>
                    {isLoading ? (
                        <TableRow withHover={false} lastItemBorderBottom={false}>
                            <TableBodyCell colSpan={4} css={getTableEmptyStyles}>
                                <Spinner />
                            </TableBodyCell>
                        </TableRow>
                    ) : items.length > 0 ? (
                        items.map((transaction, index) => (
                            <TableRow
                                key={`${transaction.orderReference}${index}`}
                                withHover={false}
                                lastItemBorderBottom={false}
                            >
                                <TableBodyCell>
                                    <CroppableContent id={`${transaction.orderReference}${index}`}>
                                        {transaction.orderReference}
                                    </CroppableContent>
                                </TableBodyCell>
                                <TableBodyCell>{dateFormatter.format(new Date(transaction.orderDate))}</TableBodyCell>
                                <TableBodyCell>
                                    {Currency.format(transaction.totalPrice, transaction.currency)}
                                </TableBodyCell>
                                <TableBodyCell>
                                    {t(`transactions.type.${getTransactionTypeTranslationKey(transaction.type)}`)}
                                </TableBodyCell>
                            </TableRow>
                        ))
                    ) : isEmptySearch ? (
                        <TableRow withHover={false} lastItemBorderBottom={false}>
                            <TableBodyCell colSpan={4}>
                                <EmptyState
                                    title={t('table.no-results.title')}
                                    subtitle={t('table.no-results.message')}
                                    isElevated={false}
                                />
                            </TableBodyCell>
                        </TableRow>
                    ) : (
                        <TableRow withHover={false} lastItemBorderBottom={false}>
                            <TableBodyCell colSpan={4} css={getTableEmptyStyles}>
                                {t('transactions.table.empty-message')}
                            </TableBodyCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </>
    );
}

function getTransactionTypeTranslationKey(type: string) {
    return type.toLowerCase().replace(/_/g, '-');
}

function generateSearchParams(filters: TransactionFilters, page?: number): URLSearchParams {
    const searchParams = new URLSearchParams();

    if (filters.id) {
        searchParams.set('id', filters.id);
    }

    if (filters.from) {
        searchParams.set('from', filters.from);
    }

    if (filters.to) {
        searchParams.set('to', filters.to);
    }

    if (page && page > 1) {
        searchParams.set('page', page.toString());
    }

    return searchParams;
}
